<template>
    <div>
        <client-modal :enableCloseButton="true" :show="isShowingErrorModal" @close="() => isShowingErrorModal = false">
            <div class="p-10">
                <div class="flex flex-col items-center">
                    <p class="text-center text-gray font-avertaLight mb-2">
                        Ops! Något gick fel.
                    </p>

                    <ul class="text-center">
                        <li v-for="error in form.errors">
                            {{ error }}
                        </li>
                    </ul>
                </div>
            </div>
        </client-modal>

        <client-modal :enableCloseButton="true" :show="isShowingCreatedModal" @close="() => isShowingCreatedModal = false">
            <div class="p-10 pt-0">
                <div class="flex flex-col items-center">
                    <img src="/assets/images/splashes/dunder.svg" class="w-64 h-64" />

                    <p class="text-center text-gray font-avertaLight -mt-3">
                        Bra jobbat! Nu är kampanjen klar och på startdatum går den live! Nu kööör vi!
                    </p>
                </div>
            </div>
        </client-modal>

        <client-modal :full-page="true" :show="isShowingPreviewModal" @close="isShowingPreviewModal = false">
            <div class="bg-white">
                <campaign-preview
                    ref="preview"
                    :data="previewData"
                    :company="company"
                    @close="isShowingPreviewModal = false"></campaign-preview>
            </div>
        </client-modal>

        <div class="flex mb-4">
            <inertia-link :href="`/kund/${$page.props.currentCompany.slug}/kampanjer`" class="inline-flex items-center focus:outline-none transition-all text-sm hover:bg-red border border-red text-red hover:text-white rounded-md px-4 py-2 font-semibold tracking-wide">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                Tillbaka
            </inertia-link>

            <a v-if="campaign.status === 'active'" :href="campaign.fk_url" target="_blank" class="inline-flex items-center ml-auto focus:outline-none transition-all text-sm hover:bg-secondaryBlue border border-secondaryBlue text-secondaryBlue hover:text-white rounded-md px-4 py-2 font-semibold tracking-wide ml-auto">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Visa på familjekortet.se
            </a>
        </div>

        <div class="flex flex-col md:flex-row md:h-12 items-center mb-6 md:mb-2">
            <h2 class="text-md md:text-2xl text-red font-avertaBold"><span class="uppercase">Mina sidor Företag</span> / <span class="font-averta">Ändra kampanj</span></h2>
        </div>

        <div class="text-sm text-gray mb-10">
            Här kan du enkelt göra mindre ändringar i din kampanj som du redan har skapat. Du kan t.ex. förlänga och ändra start- & slutdatum, eller ändra i texten. Byter du erbjudande, så rekommenderar vi att du lägger upp en ny kampanj, istället för att ändra i en befintlig. Detta då en ny behandlas och kan lyftas bland nyheter och senast inkommet.
        </div>

        <div v-if="campaign.status === 'expired'" class="mb-4 bg-secondaryBlue text-white px-4 py-2 rounded-md">
            <div class="flex justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Denna kampanj har löpt ut, för att återaktivera den så <a href="#" class="underline ml-1" @click="isShowingSettingsSidebarPopup = true">väljer du ett nytt slutdatum.</a>
            </div>
            <div class="text-center text-sm mt-2">
                Tänk på att det kan ta upp till 5 minuter innan ändringen slår igenom efter du har uppdaterat slutdatumet.
            </div>
        </div>

        <div class="bg-white my-6 w-full px-4 py-3 rounded-md shadow-sm flex sticky top-[82px] z-[10]">
            <secondary-button @click="startPreview" class="ml-2">
                Förhandsgranska
            </secondary-button>

            <primary-button :loading="form.processing" @click="saveCampaign" class="ml-auto">
                Spara
            </primary-button>
        </div>

        <div>
            <campaign-type
                v-model:webshopUrl="form.url"
                v-model:campaignType="form.type"
                v-model:selectedCities="form.cities"
                :availableCities="availableCities"
                :availableTypes="availableTypes"
                :edit-mode="true"
                :errors="form.errors"></campaign-type>

            <coupon-code-type
                :campaignType="form.type"
                v-model:couponCode="form.coupon_code"
                v-model:couponType="form.coupon_code_type_id"
                :edit-mode="true"
                :errors="form.errors"></coupon-code-type>

            <general-settings
                v-model:startDate="form.start_date"
                v-model:endDate="form.end_date"
                v-model:selectedCategories="form.categories"
                v-model:isUsingNoEndDate="form.isUsingNoEndDate"
                v-model:isValentineCampaign="form.valentine"
                :availableCategories="availableCategories"
                :edit-mode="true"></general-settings>
        </div>

        <campaign-editor
            v-model:image="main_image"
            v-model:heading="form.heading"
            v-model:desc="form.description"
            @error-resolved="(field) => form.errors[field] = null"
            :errors="{
                heading: form.errors.heading,
                description: form.errors.description,
            }"></campaign-editor>
    </div>
</template>

<script>
import ClientLayout from '@/Layouts/Client';
import Popper from 'vue3-popper';
import ClientModal from '@/components/client/Modal';
import SidebarPopup from '@/components/client/SidebarPopup';
import CouponCodeType from '@/Pages/Client/Campaigns/CouponCodeType';
import CampaignType from '@/Pages/Client/Campaigns/CampaignType';
import GeneralSettings from '@/Pages/Client/Campaigns/GeneralSettings';
import CampaignEditor from '@/Pages/Client/Campaigns/CampaignEditor';
import CampaignPreview from '@/Pages/Client/Campaigns/Preview';
import PrimaryButton from '@/components/client/Buttons/PrimaryButton';
import SecondaryButton from '@/components/client/Buttons/SecondaryButton';
import { reactive } from 'vue';

export default {
    props: ['campaign', 'availableCities', 'availableCategories', 'availableTypes', 'company'],

    layout: ClientLayout,

    components: {
        Popper, CouponCodeType, CampaignType, GeneralSettings, CampaignEditor, CampaignPreview, ClientModal, SidebarPopup, PrimaryButton, SecondaryButton
    },

    data() {
        return {
            isShowingErrorModal: false,
            isShowingCreatedModal: false,
            isShowingPreviewModal: false,
            previewData: {},
            main_image: { image: null, image_crop: null, file: null, deleted: false, },
            form: this.$inertia.form({
                heading: null,
                isUsingNoEndDate: false,
                start_date: null,
                end_date: null,
                description: null,
                url: null,
                cities: [],
                categories: [],
                type: null,
                coupon_code_type_id: null,
                coupon_code: null,
                main_image: { file: null, deleted: false, },
                valentine: false,
            }),
        };
    },

    watch: {
        main_image: {
            deep: true,
            handler(currentValue, oldValue) {
                // Only set the required values to avoid large upload size
                this.form.main_image.cropper = currentValue.cropper;
                this.form.main_image.file = currentValue.file;
                this.form.main_image.deleted = currentValue.deleted;
            }
        }
    },

    created () {
        this.form.start_date = this.campaign.start_date;
        this.form.end_date = this.campaign.end_date;
        this.form.url = this.campaign.url;
        this.form.cities = this.campaign.cities.map((c) => c.id);
        this.form.categories = this.campaign.categories.map((ca) => ca.id);
        this.form.description = this.campaign.description;
        this.form.heading = this.campaign.heading;
        this.form.type = this.campaign.type;
        this.form.coupon_code_type_id = this.campaign.coupon_code_type_id;
        this.form.coupon_code = this.campaign.coupon_code;
        this.form.valentine = this.campaign.for_valentine_year;

        if (!this.campaign.end_date) {
            this.form.isUsingNoEndDate = true;
        }

        if (this.campaign.main_image) {
            this.main_image = this.campaign.main_image;
        }

        if (this.$page.props.flash === 'campaign:created') {
            this.isShowingCreatedModal = true;
        }
    },

    methods: {
        startPreview() {
            this.previewData = {
                heading: this.form.heading,
                description: this.form.description,
                main_image: this.main_image.image_crop,
                uri: this.campaign.slug,
            };

            this.isShowingPreviewModal = true;

            setTimeout(() => this.$refs.preview.createPreview(), 300);
        },

        saveCampaign() {
            if (this.form.processing) {
                return;
            }

            this.form.post(`/kund/${this.company.slug}/kampanjer/${this.campaign.slug}`, {
                onSuccess: () => {
                    this.$moshaToast('Dina ändringar har sparats!', {
                        type: 'success',
                        timeout: 2000,
                    });
                },

                onError: () => {
                    this.isShowingErrorModal = true;
                }
            });
        },
    }
}
</script>
